import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, CheckCircle, User, Mail, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RequestAccess = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim()) {
      toast.error("Please fill in your name and email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/access-request`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        reason: reason.trim()
      });
      
      setSubmitted(true);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error(error.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-clarity-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-4">
              Request Submitted!
            </h1>
            <p className="text-slate-400 mb-6">
              Thank you for your interest in ClarityOS. We'll review your request and get back to you soon.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="bg-brand-primary text-clarity-bg font-semibold hover:bg-yellow-400"
              data-testid="back-to-login-btn"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clarity-bg relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 grid-pattern opacity-50"></div>
      
      {/* Ambient glow - hidden on mobile */}
      <div className="hidden md:block absolute top-1/4 left-1/4 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6 md:p-8">
          <button 
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Login</span>
          </button>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="text-center mb-8">
              <h1 className="text-3xl tracking-tight mb-2" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                <span className="font-bold text-white">CLARITY</span>
                <span className="font-normal text-brand-primary">OS</span>
              </h1>
              <p className="text-slate-500">Request Beta Access</p>
            </div>

            {/* Form */}
            <div className="glass rounded-2xl p-8">
              <h2 className="text-xl font-heading font-semibold text-white mb-2 text-center">
                Join the Beta Program
              </h2>
              <p className="text-slate-400 text-sm text-center mb-6">
                Fill in your details and we'll get back to you shortly.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">
                    Full Name
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-primary"
                      data-testid="name-input"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-primary"
                      data-testid="email-input"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason" className="text-slate-300">
                    Why are you interested? <span className="text-slate-500">(Optional)</span>
                  </Label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
                    <textarea
                      id="reason"
                      placeholder="Tell us about yourself and why you're interested in ClarityOS..."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                      className="w-full pl-10 pr-4 py-3 bg-clarity-subtle/50 border border-white/10 rounded-md text-white placeholder:text-slate-500 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary resize-none"
                      data-testid="reason-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-primary text-clarity-bg font-semibold hover:bg-yellow-400 disabled:opacity-50"
                  data-testid="submit-request-btn"
                >
                  {loading ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Footer */}
            <p className="text-slate-600 text-xs text-center mt-6">
              Already have access?{" "}
              <button
                onClick={() => navigate("/login")}
                className="text-brand-primary hover:underline"
              >
                Sign In
              </button>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};
